import React from 'react';
import { useSelector } from '../../../util/store/store';
import ReductionValue, { getDisplayValue } from './ReductionValue';

export interface ILiceReduction {
    stationary_lice?: number
    mobile_lice?: number
    female_lice?: number
}

interface IProps {
    value?: ILiceReduction
    valueClassName?: string
    showDetails?: boolean
    layout?: 'horizontal' | 'vertical'
}

const LAYOUT_CONFIG = {
    horizontal: {
        containerClassName: 'items-center gap-[10px]',
        emptyClassName: '',
        labelClassName: 'whitespace-nowrap text-comment',
        labelSuffix: ':',
        valueClassName: '',
        vertical: false
    },
    vertical: {
        containerClassName: 'flex-col items-center',
        emptyClassName: 'gap-[6px]',
        labelClassName: 'whitespace-nowrap text-comment font-[400]',
        labelSuffix: '',
        valueClassName: 'justify-end leading-[22px]',
        vertical: true
    }
} as const;

const getDetailsTooltip = (values: (number | undefined)[]) => <div>
    <div>Stationary: {getDisplayValue(values[0])}</div>
    <div>Mobile: {getDisplayValue(values[1])}</div>
    <div>Adult female: {getDisplayValue(values[2])}</div>
</div>;

const LiceReduction: React.FC<IProps> = ({ value, valueClassName, showDetails = true, layout = 'horizontal' }) => {
    const layoutConfig = LAYOUT_CONFIG[layout];
    const threshold = useSelector(state => state.login.user?.tenant.threshold_lice_reduction);

    if (!threshold)
        return null;

    const values = [value?.stationary_lice, value?.mobile_lice, value?.female_lice];
    const thresholds = [threshold.stationary_lice, threshold.mobile_lice, threshold.female_lice];
    const availableValues = values.filter((item): item is number => typeof item === 'number');
    const average = availableValues.length > 0
        ? availableValues.reduce((total, item) => total + item, 0) / availableValues.length
        : undefined;
    const averageClassName = [valueClassName || '', layoutConfig.valueClassName].join(' ');

    if (availableValues.length === 0)
        return <div className={[
            'flex text-[15px]',
            layoutConfig.containerClassName,
            layoutConfig.emptyClassName
        ].join(' ')}>
            <span className={layoutConfig.labelClassName}>Lice reduction{layoutConfig.labelSuffix}</span>
            <span className={averageClassName}>-</span>
        </div>;

    const availableThresholds = thresholds.filter((_item, index) => typeof values[index] === 'number');
    const averageThreshold = availableThresholds.reduce((total, item) => total + item, 0)
        / availableThresholds.length;

    return <div className={`flex text-[15px] ${layoutConfig.containerClassName}`}>
        <span className={layoutConfig.labelClassName}>Lice reduction{layoutConfig.labelSuffix}</span>
        <ReductionValue
            value={average}
            threshold={averageThreshold}
            strong
            description={showDetails
                ? 'Average lice reduction'
                : getDetailsTooltip(values)}
            className={averageClassName}
            vertical={layoutConfig.vertical}
        />
        {showDetails && <span className='flex items-center whitespace-nowrap'>
            (<span className='flex items-center gap-[10px]'>
                <ReductionValue value={values[0]} threshold={thresholds[0]} description='Stationary lice reduction' className={valueClassName} />
                <ReductionValue value={values[1]} threshold={thresholds[1]} description='Mobile lice reduction' className={valueClassName} />
                <ReductionValue value={values[2]} threshold={thresholds[2]} description='Adult female lice reduction' className={valueClassName} />
            </span>)
        </span>}
    </div>;
};

export default LiceReduction;
